import log from 'loglevel'
import { RestApiClient, TAG_USER_ROLE_ID } from 'ifield-core-components'
import ImageRoundClipper from '@/js/ImageRoundClipper.js'
import defaultIcon from '@/assets/ifield/user-image-default@3x.png'

/**
 * サムネイル種別
 */
const THUMBNAIL_TYPE = {
  // 通常
  NORMAL: 1,
  // グレースケール
  GRAYSCALE: 2,
}

/**
 * フルパスへの変換
 * @param {Number} id ユーザーID
 * @param {Number} thumbnail サムネイル種別
 * @returns {String} フルパス
 */
const toFullPath = (id, thumbnail) => {
  const url = RestApiClient.createApiUrl('download_file')
  const params = url.searchParams
  params.append('id', id)
  params.append('thumbnail', thumbnail)
  params.append('attach_file_type', 9)
  return decodeURI(url.href)
}

/**
 * マーカー画像のローダー
 */
class LocationPlayIconLoader {
  /**
   * コンストラクタ
   * @param {Number} innerSize 中の画像サイズ(px)
   * @param {Number} outlineWidth 枠線幅(px)
   * @param {Map} store 画像情報ストア
   */
  constructor({ innerSize, outlineWidth, store } = {}) {
    this.innerSize = innerSize == null ? 48 : +innerSize
    this.outlineWidth = outlineWidth == null ? 5 : +outlineWidth
    this.store = store || new Map()
  }

  /**
   * ロード
   * @param {Number} id ユーザーID
   * @param {Array.<Number>} color 枠線色
   * @param {Number} roleId ロールID
   * @returns {Object} ロード結果(atlas, mapping, cache)
   */
  async load(id, color, roleId) {
    if (!id) {
      log.warn('id is not set. load default marker.')
    }
    let profile
    if (this.store.has(id)) {
      profile = this.store.get(id)
    } else {
      profile = await this._loadFromRemote(id, roleId)
    }
    return await this._generateIcon(profile, color)
  }

  /**
   * Remoteからアイコン画像をロードする
   * @param {Number} id ユーザーID
   * @param {Number} roleId ロールID
   * @returns {Object}
   */
  async _loadFromRemote(id, roleId) {
    try {
      // タグユーザーは分析用のユーザー色を目立たせるためグレースケールで取得する
      const fullPath = toFullPath(
        id,
        roleId === TAG_USER_ROLE_ID
          ? THUMBNAIL_TYPE.GRAYSCALE
          : THUMBNAIL_TYPE.NORMAL
      )
      const img = await this._loadProfileImage(fullPath)
      const clippedImage = await ImageRoundClipper.clip(img, 'imageBitmap')
      clippedImage.id = id
      this.store.set(id, clippedImage)
      return clippedImage
    } catch {
      log.debug('failed to load icon from remote. load default icon.')
      return await this._loadDefault()
    }
  }

  /**
   * マーカーをロードする
   * @param {String} src イメージソース
   */
  _loadProfileImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'use-credentials'
      img.onload = () => resolve(img)
      img.onerror = () => reject(src)
      img.src = src
    })
  }

  /**
   * デフォルト画像をロードする
   * @returns {Object}
   */
  async _loadDefault() {
    const img = await this._loadProfileImage(defaultIcon)
    return await ImageRoundClipper.clip(img, 'imageBitmap')
  }

  /**
   * マーカー画像を生成する
   *
   * @param {Object} info 元マーカー画像ロード結果
   * @param {Array.<Number>} color 枠線色
   * @returns {Object} アイコン画像情報
   */
  async _generateIcon(info, color) {
    const { id, image } = info
    const canvas = document.createElement('canvas')
    const canvasSize = this.innerSize + this.outlineWidth * 2
    canvas.width = canvasSize
    canvas.height = canvasSize
    const ctx = canvas.getContext('2d')

    // 外周線描画
    ctx.fillStyle = `rgb(${color.join(',')})`
    ctx.beginPath()
    ctx.arc(canvasSize / 2, canvasSize / 2, canvasSize / 2, 0, Math.PI * 2)
    ctx.fill()
    // アイコン描画
    ctx.drawImage(
      image,
      this.outlineWidth,
      this.outlineWidth,
      this.innerSize,
      this.innerSize
    )

    // 画像作成
    const atlas = await createImageBitmap(
      ctx.getImageData(0, 0, canvasSize, canvasSize)
    )
    const url = canvas.toDataURL()
    canvas.remove()

    return {
      id,
      atlas,
      mapping: {
        x: 0,
        y: 0,
        width: canvasSize,
        height: canvasSize,
        anchorX: 0.5,
        anchorY: 0.5,
      },
      url,
    }
  }
}

export default LocationPlayIconLoader
